import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { BrokenPeriodCalculation, InterestAccrual, InterestCapitalisation } from '@/types';

// ── Run manual accrual ──────────────────────────────────

interface AccrueInput {
  date?: string; // Backend expects 'date', not 'accrualDate'
}

async function accrueInterest(loanId: string, input?: AccrueInput): Promise<InterestAccrual> {
  const { data } = await api.post(`/loans/${loanId}/accrual/run`, input || {});

  const result = data?.results?.[0];
  return {
    id: result?.accrualRecordId ?? result?.transactionId ?? '',
    loanId,
    accrualDate: result?.accrualDate ?? input?.date ?? new Date().toISOString(),
    amount: String(result?.dailyInterest ?? 0),
    principalBalance: String(result?.principal ?? 0),
    rate: String(result?.rate ?? 0),
    dayCountMethod: 'ACTUAL_365',
    description: result?.alreadyAccrued
      ? 'Interest already accrued for this date'
      : 'Interest accrued',
    createdAt: new Date().toISOString(),
  };
}

export function useAccrueInterest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loanId, ...input }: (AccrueInput | undefined) & { loanId: string }) =>
      accrueInterest(loanId, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['loans', vars.loanId, 'interest'] });
      qc.invalidateQueries({ queryKey: ['loans', vars.loanId, 'accruals'] });
    },
  });
}

// ── Capitalise interest ─────────────────────────────────

async function capitaliseInterest(loanId: string): Promise<InterestCapitalisation> {
  const { data } = await api.post<InterestCapitalisation>(`/loans/${loanId}/interest/capitalise`);
  return data;
}

export function useCapitaliseInterest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: capitaliseInterest,
    onSuccess: (_data, loanId) => {
      qc.invalidateQueries({ queryKey: ['loans', loanId, 'interest'] });
      qc.invalidateQueries({ queryKey: ['loans', loanId, 'accruals'] });
      qc.invalidateQueries({ queryKey: ['loans', loanId, 'capitalisations'] });
      qc.invalidateQueries({ queryKey: ['loans', loanId] });
    },
  });
}

// ── Fetch accrual history ───────────────────────────────

async function fetchAccruals(loanId: string): Promise<InterestAccrual[]> {
  const { data } = await api.get<{ accruals: Array<any> }>(`/loans/${loanId}/interest/accruals`);
  return (data?.accruals ?? []).map(accrual => ({
    id: accrual.id,
    loanId: accrual.loanId,
    accrualDate: accrual.accrualDate,
    amount: String(accrual.accrualAmount ?? accrual.amount ?? 0),
    principalBalance: String(accrual.principal ?? accrual.principalBalance ?? 0),
    rate: String(accrual.rate ?? 0),
    dayCountMethod: accrual.convention ?? accrual.dayCountMethod,
    description: accrual.notes ?? accrual.description,
    createdAt: accrual.createdAt,
  }));
}

export function useAccruals(loanId: string) {
  return useQuery({
    queryKey: ['loans', loanId, 'accruals'],
    queryFn: () => fetchAccruals(loanId),
    enabled: !!loanId,
  });
}

// ── Calculate broken period interest ────────────────────

interface BrokenPeriodInput {
  fromDate: string; // Backend BrokenPeriodDto expects fromDate/toDate
  toDate: string;
}

async function calculateBrokenPeriod(
  loanId: string,
  input: BrokenPeriodInput,
): Promise<BrokenPeriodCalculation> {
  const { data } = await api.post<BrokenPeriodCalculation>(
    `/loans/${loanId}/interest/broken-period`,
    input,
  );
  return {
    startDate: String((data as any).from ?? input.fromDate),
    endDate: String((data as any).to ?? input.toDate),
    daysCount: (data as any).days ?? (data as any).daysCount ?? 0,
    rate: Number((data as any).dailyRate ?? (data as any).rate ?? 0),
    principal: Number((data as any).principal ?? 0),
    amount: Number((data as any).totalInterest ?? (data as any).amount ?? 0),
    dayCountMethod: String((data as any).dayCountMethod ?? 'ACTUAL_365'),
  };
}

export function useBrokenPeriodCalculation() {
  return useMutation({
    mutationFn: ({
      loanId,
      fromDate,
      toDate,
    }: {
      loanId: string;
      fromDate: string;
      toDate: string;
    }) => calculateBrokenPeriod(loanId, { fromDate, toDate }),
  });
}

// ── Fetch capitalisation history ────────────────────────

async function fetchCapitalisations(loanId: string): Promise<InterestCapitalisation[]> {
  const { data } = await api.get<InterestCapitalisation[]>(
    `/loans/${loanId}/interest/capitalisations`,
  );
  return data;
}

export function useCapitalisations(loanId: string) {
  return useQuery({
    queryKey: ['loans', loanId, 'capitalisations'],
    queryFn: () => fetchCapitalisations(loanId),
    enabled: !!loanId,
  });
}
